import React, { useCallback, useState } from 'react';

import { connect } from 'react-redux';

import { useImpression } from '@nerdwallet/analytics/react/impression';
import BaseOnboardingModal, {
  StepsArrayType,
  StepsInterface,
  AnalyticsInterface,
} from '@nerdwallet/calculator-onboarding-modal';
import { enableCSAT } from '@nerdwallet/nw-client-lib/csat';

import {
  postOnboardingCalculate,
  saveHomeCalculatorProfile,
} from '../../lib/actions';
import saveToProfile from '../../lib/actions/save-to-profile';
import {
  EVENT_TYPE,
  FORM_NAME,
  FORM_SUBMITTED,
  FORM_CANCELLED,
  MORTGAGE_TYPE,
  ENTITY_NAME,
} from '../../lib/analytics-constants';
import { validationsSelector } from '../../lib/selectors';
import { manualInputsSelector } from '../../lib/selectors/refinance-calculator-selectors';
import {
  hasNoErrors,
  ReducerInputsData,
  Validations,
  RefinanceCalculatorField,
} from '../../lib/utils';

import {
  MortgageAmountInput,
  MortgageLoanTermInput,
  MortgageInterestRateInput,
  MortgageOriginationYearInput,
  RefinanceLoanTermInput,
  RefinanceInterestRateInput,
  RefinanceGoalInput,
} from './inputs';

interface RefinanceOnboardingProps
  extends Pick<
    ReducerInputsData<RefinanceCalculatorField>,
    'manualInputs' | 'validations'
  > {
  location: Record<string, any>;
  browser: Record<string, any>;
  onClose: () => void;
  isOpen: boolean;
  postOnboardingCalculateAction: (isOnboardingCompleted: boolean) => void;
  saveHomeCalculatorProfileAction: (p: Record<string, any>) => void;
  saveToProfileAction: () => void;
}

const MODAL_NAME = 'onboarding';

const getMortgageAmountStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.mortgageBalance?.[0];
  const error = Boolean(errorMessage);
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_ORIGINAL_AMOUNT_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 1,
        total_pages: 7,
      },
    },
  };

  const label = 'What was your original mortgage amount?';
  const tooltip = 'The amount you borrowed.';

  return {
    title: 'Current mortgage',
    content: (
      <MortgageAmountInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent:
      'When you first take out a loan, most of your monthly payments go toward paying interest rather than principal.',
  };
};

const getMortgageLoanTermStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.currentLoanTerm?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 2,
        total_pages: 7,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.ORIGINAL,
        },
      },
    },
  };

  const label = 'What was your original mortgage term?';
  const tooltip =
    'The number of years you have to repay your loan (often 30 or 15 years).';

  return {
    title: 'Current mortgage',
    content: (
      <MortgageLoanTermInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent:
      'Fixed-rate loans are offered in 30-, 20-, 15- and even 10-year terms. If you’re refinancing from an adjustable-rate loan, be aware that your interest rate won’t change during the life of the loan in a fixed-rate mortgage.',
  };
};

const getMortgageInterestRateStep = (
  validations: Validations
): StepsInterface => {
  const errorMessage = validations?.currentMortgageInterestRate?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_INTEREST_RATE_APR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 3,
        total_pages: 7,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.ORIGINAL,
        },
      },
    },
  };

  const label = 'What was your original mortgage interest rate?';
  const tooltip =
    'You can find the interest rate (and the amount and term) on your loan paperwork.';

  return {
    title: 'Current mortgage',
    content: (
      <MortgageInterestRateInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent: `Refinancing could help lower your monthly payments 
        considerably if your current loan’s interest rate is much higher than 
        today’s rates.`,
  };
};

const getMortgageOriginationYearStep = (
  validations: Validations
): StepsInterface => {
  const errorMessage = validations?.originationYear?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_ORIGINATION_YEAR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 4,
        total_pages: 7,
      },
    },
  };

  const label = 'What was your initial origination year?';
  const tooltip = 'The year you took out your loan.';

  return {
    title: 'Current mortgage',
    content: (
      <MortgageOriginationYearInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent: `A lower monthly payment isn’t the only reason to refinance; you can also do a
        cash-out refinance, switch loan types, or eliminate private mortgage insurance.`,
  };
};

const getRefinanceLoanTermStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.refinanceLoanTerm?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 5,
        total_pages: 7,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };

  const label = 'What is your new mortgage term?';
  const tooltip =
    'The number of years you will repay your new loan. A shorter term will give a higher payment but you may pay less interest over time.';

  return {
    title: 'New mortgage',
    content: (
      <RefinanceLoanTermInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent: `A shorter-term loan saves money in interest, but the monthly payments are higher.
        Choose a longer-term loan if you need lower monthly payments. But remember — you’ll
        pay more in interest over the life of the loan.`,
  };
};

const getRefinanceInterestRateStep = (
  validations: Validations
): StepsInterface => {
  const errorMessage = validations?.refinanceInterestRate?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_INTEREST_RATE_APR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 6,
        total_pages: 7,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };

  const label = 'What is your new mortgage interest rate?';
  const tooltip = 'The new interest rate.';

  return {
    title: 'New mortgage',
    content: (
      <RefinanceInterestRateInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent: `The current refinance rate is an average provided to NerdWallet by Zillow.
    Your actual rate will depend largely on your credit score and credit history.
    Make sure both are in good standing by getting your most recent credit report
    and addressing any errors or issues.`,
  };
};

const getRefinanceGoalStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.refinanceGoal?.[0];
  const error = Boolean(errorMessage);

  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: MODAL_NAME,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_REFINANCE_PURPOSE_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR,
        page_number: 7,
        total_pages: 7,
      },
    },
  };

  const label = 'When refinancing, which is more important to you?';
  const tooltip =
    'You can change the answer later without having to start over.';

  return {
    title: 'New mortgage',
    content: (
      <RefinanceGoalInput
        analytics={analyticsPayload}
        label={label}
        tooltip={tooltip}
      />
    ),
    isNextDisabled: error,
    secondaryContent:
      'Refinancing can decrease your monthly mortgage payment. Alternatively, you can decrease your total mortgage interest. Sometimes you can save on both.',
  };
};

const generateSteps = (validations: Validations): StepsArrayType => {
  const steps: StepsArrayType = [
    getMortgageAmountStep(validations),
    getMortgageLoanTermStep(validations),
    getMortgageInterestRateStep(validations),
    getMortgageOriginationYearStep(validations),
    getRefinanceLoanTermStep(validations),
    getRefinanceInterestRateStep(validations),
    getRefinanceGoalStep(validations),
  ];

  return steps;
};

const RefinanceOnboardingModal: React.FC<RefinanceOnboardingProps> = (
  props
) => {
  const {
    browser,
    isOpen,
    location,
    manualInputs,
    onClose: onCloseProp,
    postOnboardingCalculateAction,
    saveHomeCalculatorProfileAction,
    saveToProfileAction,
    validations,
  } = props;

  const [step, updateStep] = useState(0);

  const errorsString = JSON.stringify(validations);
  const inputsValues = JSON.stringify(manualInputs);

  const onClose = useCallback(
    (wasSubmitted: boolean) => {
      // prevent closing if there's any error
      if (!hasNoErrors(validations)) return;

      postOnboardingCalculateAction(wasSubmitted);
      // Even if the user has exited out we still count it as having completed onboarding

      saveHomeCalculatorProfileAction({
        hasCompletedOnboardingRc: true,
        ...manualInputs,
      });
      saveToProfileAction();

      if (!browser.isMobile && !browser.isTablet) {
        enableCSAT();
      }
      onCloseProp();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errorsString,
      location.pathname,
      location.search,
      location.hash,
      inputsValues,
    ]
  );

  const onStepChanged = useCallback((newStep: number) => {
    // we need to update the step value so we trigger a new impression
    updateStep(newStep);
  }, []);

  const steps = generateSteps(validations);
  const modalTitle = 'Refinance calculator';
  const analyticsProps: AnalyticsInterface = {
    enabled: true,
    onOpen: {
      legacy: {
        formName: MODAL_NAME,
        fieldName: 'load_onboardingmodal_open',
      },
    },
    onStepChanged: {
      legacy: {
        formName: MODAL_NAME,
        fieldName: (stepNumber) =>
          `gotopage${stepNumber}_onboardingmodal_submit`,
      },
    },
    onSubmit: {
      legacy: {
        formName: MODAL_NAME,
        fieldName: 'onbsubmit_onboardingmodal_submit',
      },
      bedrock: {
        eventType: FORM_SUBMITTED,
        form_name: ENTITY_NAME.MRT_REFI_CALCULATOR,
      },
    },
    onClose: {
      legacy: {
        formName: MODAL_NAME,
        fieldName: 'onbcancel_onboardingmodal_cancel',
      },
      bedrock: {
        eventType: FORM_CANCELLED,
        form_name: ENTITY_NAME.MRT_REFI_CALCULATOR,
      },
    },
  };

  const { ref: impressionRef } = useImpression({
    eventType: EVENT_TYPE.FORM_IMPRESSION,
    entityName: ENTITY_NAME.MRT_REFI_CALCULATOR,
    payload: {
      section_name: ENTITY_NAME.MRT_REFI_CALCULATOR,
      form_name: ENTITY_NAME.MRT_REFI_CALCULATOR,
      page_number: step + 1,
      total_pages: steps.length,
    },
  });

  return (
    <div>
      <BaseOnboardingModal
        onClose={onClose}
        onStepChanged={onStepChanged}
        isOpen={isOpen}
        modalTitle={modalTitle}
        steps={steps}
        analytics={analyticsProps}
        ref={impressionRef}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  validations: validationsSelector(state),
});

const mapDispatchToProps = {
  postOnboardingCalculateAction: postOnboardingCalculate,
  saveHomeCalculatorProfileAction: saveHomeCalculatorProfile,
  saveToProfileAction: saveToProfile,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RefinanceOnboardingModal);
